/**
 * Modos de transporte do planeamento (módulo puro — sem IA, sem Maps).
 *
 * A mota é um modo INDEPENDENTE: partilha a rede rodoviária com o carro, mas
 * tem regras próprias (estradas com curvas e piso bom, autonomia menor,
 * proibição de caminhos pedonais e trilhos, paragens de descanso mais curtas
 * e frequentes). Regras genéricas — nada específico de país ou destino.
 */

export type TransportMode = "walk" | "bike" | "scooter" | "motorcycle" | "car" | "transit";

/** Modos que circulam em vias rodoviárias abertas a veículos motorizados. */
export const MOTORISED_MODES: TransportMode[] = ["motorcycle", "car"];

export type TransportProfile = {
  mode: TransportMode;
  /** Perfil de encaminhamento equivalente para o Maps/Directions. */
  routingMode: "walking" | "bicycling" | "driving" | "transit";
  /** Velocidade média realista em cidade/estrada mista, km/h. */
  avgSpeedKmh: number;
  /** Autonomia típica entre reabastecimentos/descansos, em km. */
  rangeKm?: number;
  /** Minutos de pausa recomendados por cada bloco contínuo de condução. */
  restEveryMinutes?: number;
  restMinutes?: number;
  /** Precisa de estacionamento dedicado no destino? */
  needsParking: boolean;
  /** Pode entrar em zonas exclusivamente pedonais ou trilhos? */
  allowsPedestrianOnlyPaths: boolean;
};

export const TRANSPORT_PROFILES: Record<TransportMode, TransportProfile> = {
  walk: {
    mode: "walk",
    routingMode: "walking",
    avgSpeedKmh: 4.5,
    needsParking: false,
    allowsPedestrianOnlyPaths: true,
  },
  bike: {
    mode: "bike",
    routingMode: "bicycling",
    avgSpeedKmh: 14,
    needsParking: false,
    allowsPedestrianOnlyPaths: true,
  },
  scooter: {
    mode: "scooter",
    routingMode: "bicycling",
    avgSpeedKmh: 15,
    needsParking: false,
    allowsPedestrianOnlyPaths: false,
  },
  motorcycle: {
    mode: "motorcycle",
    routingMode: "driving",
    avgSpeedKmh: 55,
    rangeKm: 220,
    restEveryMinutes: 120,
    restMinutes: 15,
    needsParking: true,
    allowsPedestrianOnlyPaths: false,
  },
  car: {
    mode: "car",
    routingMode: "driving",
    avgSpeedKmh: 60,
    rangeKm: 500,
    restEveryMinutes: 150,
    restMinutes: 20,
    needsParking: true,
    allowsPedestrianOnlyPaths: false,
  },
  transit: {
    mode: "transit",
    routingMode: "transit",
    avgSpeedKmh: 30,
    needsParking: false,
    allowsPedestrianOnlyPaths: true,
  },
};

export type TransportPlanLike = {
  primary: TransportMode;
  secondary?: readonly TransportMode[];
  hybrid?: boolean;
};

export type TransportExecution = {
  /** Modo usado nas deslocações longas entre bairros/zonas. */
  betweenZones: TransportMode;
  /** Modo usado dentro de cada grupo compacto de paragens. */
  withinZone: TransportMode;
  betweenZonesRoutingMode: TransportProfile["routingMode"];
  withinZoneRoutingMode: TransportProfile["routingMode"];
};

/**
 * Converte o pedido multimodal numa regra executável. O modo principal move o
 * viajante entre zonas; uma caminhada explicitamente aceite é usada apenas
 * dentro dos grupos locais, sem transformar toda a rota numa rota a pé.
 */
export function resolveTransportExecution(plan: TransportPlanLike): TransportExecution {
  const betweenZones = plan.primary;
  const withinZone = plan.secondary?.includes("walk") ? "walk" : betweenZones;
  return {
    betweenZones,
    withinZone,
    betweenZonesRoutingMode: routingModeFor(betweenZones),
    withinZoneRoutingMode: routingModeFor(withinZone),
  };
}

const fold = (value: string) =>
  (value ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/gu, "")
    .toLowerCase();

/** Deteta "de mota", "by motorbike", "in moto", … no texto do pedido. */
export function detectMotorcycleMode(text: string): boolean {
  const t = fold(text ?? "");
  if (!t.trim()) return false;
  return [
    /\b(?:de|em|na|com\s+a)\s+(?:mota|moto|motociclo|motocicleta)\b/u,
    /\bmotard\w*\b/u,
    /\b(?:by|on)\s+(?:motorbike|motorcycle|bike\s+trip\s+on\s+a\s+motorcycle)\b/u,
    /\bmotorcycle\s+(?:trip|tour|route|ride|road\s*trip)\b/u,
    /\bmotorbike\b/u,
    /\ben\s+moto\b|\bviaje\s+en\s+moto\b/u,
    /\ba\s+moto\b|\bvoyage\s+a\s+moto\b/u,
    /\bin\s+moto\b|\bviaggio\s+in\s+moto\b/u,
    /\bmit\s+dem\s+motorrad\b|\bmotorrad\w*\b/u,
  ].some((re) => re.test(t));
}

/** Perfil de rota a usar no Maps para um modo. */
export function routingModeFor(mode: TransportMode): TransportProfile["routingMode"] {
  return (TRANSPORT_PROFILES[mode] ?? TRANSPORT_PROFILES.car).routingMode;
}

/** Uma paragem que exige caminho pedonal/trilho não serve para mota nem carro. */
export function isStopReachable(
  mode: TransportMode,
  stop: { pedestrianOnly?: boolean | null; category?: string | null } | null | undefined,
): boolean {
  if (!stop) return false;
  const profile = TRANSPORT_PROFILES[mode] ?? TRANSPORT_PROFILES.car;
  if (profile.allowsPedestrianOnlyPaths) return true;
  if (stop.pedestrianOnly) return false;
  const category = fold(stop.category ?? "");
  return !/\b(trail|trilho|hiking|footpath|pedestrian|passadico|boardwalk)\b/u.test(category);
}

/** Pausas obrigatórias de descanso/combustível para um dia de condução. */
export function requiredRestBreaks(
  mode: TransportMode,
  drivingMinutes: number,
): Array<{ afterMinutes: number; minutes: number; reason: "rest" | "fuel" }> {
  const profile = TRANSPORT_PROFILES[mode];
  if (!profile?.restEveryMinutes || drivingMinutes <= profile.restEveryMinutes) return [];
  const breaks: Array<{ afterMinutes: number; minutes: number; reason: "rest" | "fuel" }> = [];
  let elapsed = profile.restEveryMinutes;
  let index = 0;
  while (elapsed < drivingMinutes) {
    index += 1;
    breaks.push({
      afterMinutes: elapsed,
      minutes: profile.restMinutes ?? 15,
      reason: mode === "motorcycle" && index % 2 === 0 ? "fuel" : "rest",
    });
    elapsed += profile.restEveryMinutes;
  }
  return breaks;
}

/** Instruções determinísticas para juntar ao pedido enviado à IA. */
export function motorcycleDirective(language = "pt"): string {
  const map: Record<string, string> = {
    pt: [
      "Viagem de mota: planeia apenas por estradas abertas a motociclos.",
      "Prefere estradas nacionais e troços com curvas e paisagem em vez de autoestrada monótona.",
      "Não incluias trilhos, passadiços nem zonas exclusivamente pedonais como paragens de condução.",
      "Indica estacionamento para mota em cada paragem e prevê pausas de descanso a cada ~2 horas.",
      "Assinala onde reabastecer quando o troço passar dos 200 km sem localidades.",
      "Evita troços de montanha com previsão de chuva forte ou gelo.",
      "Não proponhas paragens de autocarro, estações ferroviárias, horários ou reservas de transporte público.",
    ].join(" "),
    en: [
      "Motorcycle trip: plan only on roads open to motorbikes.",
      "Prefer scenic national roads with curves over monotonous motorway.",
      "Never include trails, boardwalks or pedestrian-only areas as riding stops.",
      "Mention motorcycle parking at each stop and add a rest break roughly every 2 hours.",
      "Flag refuelling points when a leg exceeds 200 km without towns.",
      "Avoid mountain sections with heavy rain or ice forecast.",
    ].join(" "),
  };
  return map[(language || "pt").slice(0, 2).toLowerCase()] ?? map.en;
}

/** Etiqueta visível do modo, por idioma. */
export function transportModeLabel(mode: TransportMode, language = "pt"): string {
  const labels: Record<string, Record<TransportMode, string>> = {
    pt: {
      walk: "A pé",
      bike: "Bicicleta",
      scooter: "Trotinete",
      motorcycle: "Mota",
      car: "Carro",
      transit: "Transportes",
    },
    en: { walk: "Walking", bike: "Bike", scooter: "Scooter", motorcycle: "Motorcycle", car: "Car", transit: "Transit" },
    es: { walk: "A pie", bike: "Bici", scooter: "Patinete", motorcycle: "Moto", car: "Coche", transit: "Transporte" },
    fr: { walk: "À pied", bike: "Vélo", scooter: "Trottinette", motorcycle: "Moto", car: "Voiture", transit: "Transports" },
    it: { walk: "A piedi", bike: "Bici", scooter: "Monopattino", motorcycle: "Moto", car: "Auto", transit: "Trasporti" },
    de: { walk: "Zu Fuß", bike: "Rad", scooter: "E-Scooter", motorcycle: "Motorrad", car: "Auto", transit: "ÖPNV" },
  };
  const set = labels[(language || "pt").slice(0, 2).toLowerCase()] ?? labels.en;
  return set[mode] ?? set.car;
}
